import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API_BASE } from "../../apiConfig";

export default function SuperAdminDashboard() {
  const navigate = useNavigate();
  const token = sessionStorage.getItem("super_token");

  const [admins, setAdmins] = useState([]);

  // Search + Filter
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("ALL");

  // Create Admin directly
  const [newUsername, setNewUsername] = useState("");
  const [newPassword, setNewPassword] = useState("");

  // Toast
  const [showToast, setShowToast] = useState(false);
  const [toastMsg, setToastMsg] = useState("");
  const [toastType, setToastType] = useState("dark");

  // Confirm delete
  const [deleteId, setDeleteId] = useState(null);

  // Change password modal (simple UI)
  const [pwdId, setPwdId] = useState(null);
  const [pwdUsername, setPwdUsername] = useState("");
  const [pwdNew, setPwdNew] = useState("");

  // Pagination
  const [page, setPage] = useState(1);
  const pageSize = 5;

  const showToastMessage = (msg, type = "dark", duration = 2000) => {
    setToastMsg(msg);
    setToastType(type);
    setShowToast(true);
    setTimeout(() => setShowToast(false), duration);
  };

  useEffect(() => {
    if (!token) navigate("/superadmin-login");
    loadAdmins();
    // eslint-disable-next-line
  }, []);

  const loadAdmins = async () => {
    try {
      const res = await axios.get(`${API_BASE}/getPendingAdmins.php`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.data.status === "success") {
        setAdmins(res.data.admins);
      } else {
        showToastMessage("❌ Failed to load admins", "danger");
      }
    } catch (err) {
      showToastMessage("❌ Server error while loading admins", "danger");
    }
  };

  const approveAdmin = async (id) => {
    try {
      const res = await axios.post(
        `${API_BASE}/approveAdmin.php`,
        { id },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data.status === "success") {
        showToastMessage("✅ Admin Approved", "success");
        loadAdmins();
      } else {
        showToastMessage(`❌ ${res.data.message}`, "danger");
      }
    } catch (err) {
      showToastMessage("❌ Approval failed", "danger");
    }
  };

  const rejectAdmin = async (id) => {
    try {
      const res = await axios.post(
        `${API_BASE}/rejectAdmin.php`,
        { id },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data.status === "success") {
        showToastMessage("🚫 Admin Rejected", "warning");
        loadAdmins();
      } else {
        showToastMessage(`❌ ${res.data.message}`, "danger");
      }
    } catch (err) {
      showToastMessage("❌ Reject failed", "danger");
    }
  };

  const requestDelete = (id) => {
    setDeleteId(id);
    showToastMessage("🗑️ Confirm delete below (Yes / No)", "warning", 3500);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;

    try {
      const res = await axios.post(
        `${API_BASE}/deleteAdmin.php`,
        { id: deleteId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data.status === "success") {
        showToastMessage("✅ Admin Deleted", "success");
        setDeleteId(null);
        loadAdmins();
      } else {
        showToastMessage(`❌ ${res.data.message}`, "danger");
      }
    } catch (err) {
      showToastMessage("❌ Delete failed", "danger");
    }
  };

  const cancelDelete = () => {
    setDeleteId(null);
    showToastMessage("❎ Delete cancelled", "info");
  };

  const superCreateAdmin = async () => {
    if (!newUsername.trim() || !newPassword.trim()) {
      showToastMessage("⚠️ Username & Password required", "warning");
      return;
    }

    try {
      const res = await axios.post(
        `${API_BASE}/superCreateAdmin.php`,
        { username: newUsername, password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data.status === "success") {
        showToastMessage("✅ Admin Created & Approved", "success", 2500);
        setNewUsername("");
        setNewPassword("");
        loadAdmins();
      } else {
        showToastMessage(`❌ ${res.data.message}`, "danger", 2500);
      }
    } catch (err) {
      showToastMessage("❌ Failed to create admin", "danger", 2500);
    }
  };

  const openPasswordBox = (admin) => {
    setPwdId(admin.id);
    setPwdUsername(admin.username);
    setPwdNew("");
  };

  const changePassword = async () => {
    if (!pwdNew.trim()) {
      showToastMessage("⚠️ Enter new password", "warning");
      return;
    }

    try {
      const res = await axios.post(
        `${API_BASE}/changeAdminPassword.php`,
        { id: pwdId, newPassword: pwdNew },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data.status === "success") {
        showToastMessage("✅ Password updated", "success", 2500);
        setPwdId(null);
        setPwdUsername("");
        setPwdNew("");
      } else {
        showToastMessage(`❌ ${res.data.message}`, "danger", 2500);
      }
    } catch (err) {
      showToastMessage("❌ Failed to update password", "danger", 2500);
    }
  };

  const logout = () => {
    sessionStorage.removeItem("super_token");
    navigate("/superadmin-login");
  };

  const filteredAdmins = useMemo(() => {
    let list = [...admins];

    if (filterStatus !== "ALL") {
      list = list.filter((a) => a.status === filterStatus);
    }

    if (search.trim()) {
      const s = search.toLowerCase();
      list = list.filter((a) => a.username.toLowerCase().includes(s));
    }

    return list;
  }, [admins, search, filterStatus]);

  // pagination logic
  const totalPages = Math.ceil(filteredAdmins.length / pageSize);
  const paginatedAdmins = filteredAdmins.slice(
    (page - 1) * pageSize,
    page * pageSize
  );

  // reset page when search/filter changes
  useEffect(() => {
    setPage(1);
  }, [search, filterStatus]);

  return (
    <div className="container py-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="fw-bold mb-0">Super Admin Panel 👑</h2>
        <button className="btn btn-danger" onClick={logout}>
          Logout
        </button>
      </div>

      {/* Create Admin */}
      <div className="card shadow-sm border-0 rounded-4 mb-4">
        <div className="card-body p-4">
          <h4 className="fw-bold mb-3">Create Admin (Direct Approval)</h4>

          <div className="row g-2">
            <div className="col-md-4">
              <input
                className="form-control"
                placeholder="New Username"
                value={newUsername}
                onChange={(e) => setNewUsername(e.target.value)}
              />
            </div>

            <div className="col-md-4">
              <input
                className="form-control"
                placeholder="New Password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </div>

            <div className="col-md-4">
              <button className="btn btn-success w-100" onClick={superCreateAdmin}>
                Create & Approve
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Search + Filter */}
      <div className="card shadow-sm border-0 rounded-4 mb-3">
        <div className="card-body p-4">
          <div className="row g-2 align-items-center">
            <div className="col-md-6">
              <input
                className="form-control"
                placeholder="Search by username..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <div className="col-md-3">
              <select
                className="form-select"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="ALL">All</option>
                <option value="PENDING">Pending</option>
                <option value="APPROVED">Approved</option>
                <option value="REJECTED">Rejected</option>
              </select>
            </div>

            <div className="col-md-3">
              <button className="btn btn-primary w-100" onClick={loadAdmins}>
                Refresh List
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete confirmation */}
      {deleteId && (
        <div className="alert alert-warning shadow-sm d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2">
          <div>
            <b>Are you sure?</b> Delete Admin ID: <b>{deleteId}</b>
          </div>
          <div className="d-flex gap-2">
            <button className="btn btn-danger" onClick={confirmDelete}>
              Yes Delete
            </button>
            <button className="btn btn-secondary" onClick={cancelDelete}>
              No
            </button>
          </div>
        </div>
      )}

      {/* Admin List */}
      <div className="card shadow-sm border-0 rounded-4">
        <div className="card-body p-4">
          <h4 className="fw-bold mb-3">Admin Approvals</h4>

          {paginatedAdmins.length === 0 ? (
            <div className="alert alert-info">No admins found.</div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table table-bordered align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>ID</th>
                      <th>Username</th>
                      <th>Status</th>
                      <th>Registered</th>
                      <th style={{ width: "320px" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedAdmins.map((a) => (
                      <tr key={a.id}>
                        <td>{a.id}</td>
                        <td>{a.username}</td>
                        <td>
                          <span
                            className={`badge ${
                              a.status === "APPROVED"
                                ? "bg-success"
                                : a.status === "REJECTED"
                                ? "bg-danger"
                                : "bg-warning text-dark"
                            }`}
                          >
                            {a.status}
                          </span>
                        </td>
                        <td>{a.created_at || "-"}</td>

                        <td className="d-flex gap-2 flex-wrap">
                          {a.status !== "APPROVED" && (
                            <button
                              className="btn btn-success btn-sm"
                              onClick={() => approveAdmin(a.id)}
                            >
                              Approve
                            </button>
                          )}

                          {a.status !== "REJECTED" && (
                            <button
                              className="btn btn-warning btn-sm"
                              onClick={() => rejectAdmin(a.id)}
                            >
                              Reject
                            </button>
                          )}

                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => openPasswordBox(a)}
                          >
                            Change Password
                          </button>

                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => requestDelete(a.id)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="d-flex justify-content-between align-items-center mt-3">
                <small className="text-muted">
                  Page {page} of {totalPages || 1}
                </small>

                <div className="d-flex gap-2">
                  <button
                    className="btn btn-outline-primary btn-sm"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    Prev
                  </button>
                  <button
                    className="btn btn-outline-primary btn-sm"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Change Password Box */}
      {pwdId && (
        <div className="card shadow-sm border-0 rounded-4 mt-4">
          <div className="card-body p-4">
            <h5 className="fw-bold mb-2">Change Password</h5>
            <p className="mb-2">
              Admin: <b>{pwdUsername}</b> (ID: {pwdId})
            </p>

            <div className="row g-2">
              <div className="col-md-6">
                <input
                  className="form-control"
                  placeholder="Enter new password"
                  type="password"
                  value={pwdNew}
                  onChange={(e) => setPwdNew(e.target.value)}
                />
              </div>
              <div className="col-md-6 d-flex gap-2">
                <button className="btn btn-success" onClick={changePassword}>
                  Update
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setPwdId(null);
                    setPwdUsername("");
                    setPwdNew("");
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      <div
        className={`toast align-items-center text-bg-${toastType} border-0 position-fixed bottom-0 end-0 m-3 ${
          showToast ? "show" : "hide"
        }`}
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
        style={{ zIndex: 9999 }}
      >
        <div className="d-flex">
          <div className="toast-body">{toastMsg}</div>
          <button
            type="button"
            className="btn-close btn-close-white me-2 m-auto"
            aria-label="Close"
            onClick={() => setShowToast(false)}
          ></button>
        </div>
      </div>
    </div>
  );
}
